import React, { useState, useEffect } from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  TablePagination,
  InputAdornment
} from '@material-ui/core'
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  SwapHoriz as ConvertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraIcon,
  BusinessCenter as BusinessCardIcon
} from '@material-ui/icons'
import { toast } from 'react-toastify'
import axios from 'axios'
import moment from 'moment'
import LeadDetailModal from './LeadDetailModal'
import LeadEditModal from './LeadEditModal'
import LeadConvertModal from './LeadConvertModal'
import './LeadsDashboard.module.scss'

const LeadsDashboard = () => {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({
    total: 0,
    statusCounts: {
      new: 0,
      contacted: 0,
      qualified: 0,
      converted: 0,
      lost: 0
    },
    conversionRate: 0
  })
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    startDate: null,
    endDate: null,
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  })
  const [totalCount, setTotalCount] = useState(0)
  
  // Modals
  const [selectedLead, setSelectedLead] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [convertModalOpen, setConvertModalOpen] = useState(false)

  // Fetch leads
  const fetchLeads = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        status: filters.status,
        search: filters.search,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      })
      
      if (filters.startDate) {
        params.append('startDate', filters.startDate)
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate)
      }

      const response = await axios.get(`/api/company/leads?${params}`)
      
      if (response.data.success) {
        setLeads(response.data.data)
        setTotalCount(response.data.pagination.total)
      }
    } catch (error) {
      console.error('Error fetching leads:', error)
      toast.error('Failed to fetch leads')
    } finally {
      setLoading(false)
    }
  }

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.startDate) {
        params.append('startDate', filters.startDate)
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate)
      }

      const response = await axios.get(`/api/company/leads/stats/summary?${params}`)
      
      if (response.data.success) {
        setStats(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  useEffect(() => {
    fetchLeads()
    fetchStats()
  }, [filters.page, filters.limit, filters.status, filters.sortBy, filters.sortOrder])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchLeads()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [filters.search])

  // Handle actions
  const handleView = (lead) => {
    setSelectedLead(lead)
    setDetailModalOpen(true)
  }

  const handleEdit = (lead) => {
    setSelectedLead(lead)
    setEditModalOpen(true)
  }

  const handleConvert = (lead) => {
    setSelectedLead(lead)
    setConvertModalOpen(true)
  }

  const handleDelete = async (lead) => {
    if (!window.confirm(`Are you sure you want to delete the lead for ${lead.companyName}?`)) {
      return
    }

    try {
      const response = await axios.delete(`/api/company/leads/${lead._id}`)
      if (response.data.success) {
        toast.success('Lead deleted successfully')
        fetchLeads()
        fetchStats()
      }
    } catch (error) {
      console.error('Error deleting lead:', error)
      toast.error(error.response?.data?.error || 'Failed to delete lead')
    }
  }

  const handleStatusChange = async (lead, newStatus) => {
    try {
      const response = await axios.patch(`/api/company/leads/${lead._id}/status`, {
        status: newStatus
      })
      
      if (response.data.success) {
        toast.success('Status updated successfully')
        fetchLeads()
        fetchStats()
      }
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Failed to update status')
    }
  }

  const handlePageChange = (event, newPage) => {
    setFilters({ ...filters, page: newPage + 1 })
  }

  const handleRowsPerPageChange = (event) => {
    setFilters({ ...filters, limit: parseInt(event.target.value, 10), page: 1 })
  }

  const getStatusColor = (status) => {
    const colors = {
      new: 'primary',
      contacted: 'info',
      qualified: 'warning',
      converted: 'success',
      lost: 'error'
    }
    return colors[status] || 'default'
  }

  const getStatusIcon = (status) => {
    if (status === 'converted') return <CheckCircleIcon fontSize="small" />
    if (status === 'lost') return <CancelIcon fontSize="small" />
    return null
  }

  return (
    <div className="leads-dashboard">
      {/* Statistics Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Leads
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                New
              </Typography>
              <Typography variant="h4" color="primary">
                {stats.statusCounts.new}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Contacted
              </Typography>
              <Typography variant="h4" style={{ color: '#2196f3' }}>
                {stats.statusCounts.contacted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Qualified
              </Typography>
              <Typography variant="h4" style={{ color: '#ff9800' }}>
                {stats.statusCounts.qualified}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Converted
              </Typography>
              <Typography variant="h4" style={{ color: '#4caf50' }}>
                {stats.statusCounts.converted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Conversion Rate
              </Typography>
              <Typography variant="h4" style={{ color: '#4caf50' }}>
                {stats.conversionRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper style={{ padding: 16, marginBottom: 24 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              variant="outlined"
              size="small"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              variant="outlined"
              size="small"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <IconButton onClick={() => { fetchLeads(); fetchStats(); }}>
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Leads Table */}
      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          {lead.companyName}
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap" marginTop={0.5}>
                          {lead.leftFlyer && (
                            <Chip label="Left Flyer" size="small" style={{ marginRight: 4 }} />
                          )}
                          {lead.spokeWithSomeone && (
                            <Chip label="Spoke" size="small" color="primary" style={{ marginRight: 4 }} />
                          )}
                          {lead.photos && lead.photos.length > 0 && (
                            <Chip 
                              icon={<CameraIcon />} 
                              label={`${lead.photos.length} photo${lead.photos.length > 1 ? 's' : ''}`} 
                              size="small" 
                              variant="outlined"
                              style={{ marginRight: 4 }}
                            />
                          )}
                          {lead.ocrPhotos && Object.keys(lead.ocrPhotos).filter(key => lead.ocrPhotos[key]).length > 0 && (
                            <Chip 
                              icon={<BusinessCardIcon />} 
                              label={`OCR`} 
                              size="small" 
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <LocationIcon fontSize="small" style={{ marginRight: 4 }} />
                        {lead.address}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {lead.contactName && (
                        <Box>
                          <Typography variant="body2">{lead.contactName}</Typography>
                          {lead.contactPhone && (
                            <Box display="flex" alignItems="center">
                              <PhoneIcon fontSize="small" style={{ marginRight: 4 }} />
                              <Typography variant="caption">{lead.contactPhone}</Typography>
                            </Box>
                          )}
                          {lead.contactEmail && (
                            <Box display="flex" alignItems="center">
                              <EmailIcon fontSize="small" style={{ marginRight: 4 }} />
                              <Typography variant="caption">{lead.contactEmail}</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.status}
                        color={getStatusColor(lead.status)}
                        size="small"
                        icon={getStatusIcon(lead.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {moment(lead.createdAt).format('MM/DD/YYYY')}
                        </Typography>
                        {lead.lastActivityDate && (
                          <Typography variant="caption" color="textSecondary">
                            Last: {moment(lead.lastActivityDate).fromNow()}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {lead.createdBy ? 
                        `${lead.createdBy.firstName} ${lead.createdBy.lastName}` : 
                        '-'
                      }
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(lead)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {lead.contactPhone && (
                        <Tooltip title={`Call ${lead.contactPhone}`}>
                          <IconButton 
                            size="small" 
                            onClick={() => window.open(`tel:${lead.contactPhone}`, '_self')}
                            style={{ color: '#4caf50' }}
                          >
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(lead)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {lead.status !== 'converted' && (
                        <Tooltip title="Convert to Customer">
                          <IconButton size="small" onClick={() => handleConvert(lead)}>
                            <ConvertIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {lead.status !== 'converted' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(lead)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {leads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" style={{ padding: 24 }}>
                        No leads found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={filters.limit}
              page={filters.page - 1}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </>
        )}
      </Paper>

      {/* Modals */}
      {selectedLead && (
        <>
          <LeadDetailModal
            open={detailModalOpen}
            lead={selectedLead}
            onClose={() => {
              setDetailModalOpen(false)
              setSelectedLead(null)
            }}
            onEdit={() => {
              setDetailModalOpen(false)
              setEditModalOpen(true)
            }}
            onConvert={() => {
              setDetailModalOpen(false)
              setConvertModalOpen(true)
            }}
          />
          
          <LeadEditModal
            open={editModalOpen}
            lead={selectedLead}
            onClose={() => {
              setEditModalOpen(false)
              setSelectedLead(null)
            }}
            onSave={() => {
              setEditModalOpen(false)
              setSelectedLead(null)
              fetchLeads()
              fetchStats()
            }}
          />
          
          <LeadConvertModal
            open={convertModalOpen}
            lead={selectedLead}
            onClose={() => {
              setConvertModalOpen(false)
              setSelectedLead(null)
            }}
            onConvert={() => {
              setConvertModalOpen(false)
              setSelectedLead(null)
              fetchLeads()
              fetchStats()
            }}
          />
        </>
      )}
    </div>
  )
}

export default LeadsDashboard